import type { ReactNode } from 'react';
import { Route, Routes } from 'react-router-dom';
import {
  coreExtensionData,
  createFrontendModule,
  PageBlueprint,
  SubPageBlueprint,
} from '@backstage/frontend-plugin-api';
import apiDocsPlugin from '@backstage/plugin-api-docs/alpha';
import catalogPlugin from '@backstage/plugin-catalog/alpha';
import { EntityContentBlueprint, EntityHeaderLayoutBlueprint } from '@backstage/plugin-catalog-react/alpha';
import scaffolderPlugin from '@backstage/plugin-scaffolder/alpha';
import techdocsPlugin from '@backstage/plugin-techdocs/alpha';
import { ApiBrowser, CatalogBrowser, DocsBrowser, TemplateBrowser } from './browserPages';
import { TaskListPage, TaskPage, TemplateFormPage } from './ProjectPages';

// Backstage's templates sub-page holds both the list and the form behind it, and only exports
// the two together. It stays underneath as the fallback for fields our own form does not know.
const TemplateRoutes = ({ backstagePage }: { backstagePage: ReactNode }) => (
  <Routes>
    <Route index element={<TemplateBrowser />} />
    <Route path="*" element={<TemplateFormPage fallback={backstagePage} />} />
  </Routes>
);

const TaskRoutes = () => (
  <Routes>
    <Route index element={<TaskListPage />} />
    <Route path=":taskId" element={<TaskPage />} />
  </Routes>
);

// These pages replace Backstage's own lists: a table full of icon actions cannot be themed
// into the filtering pattern of the design system. Detail pages stay Backstage's.
export const browserModules = [
  createFrontendModule({
    pluginId: 'catalog',
    extensions: [
      PageBlueprint.make({
        params: {
          path: '/catalog',
          routeRef: catalogPlugin.routes.catalogIndex,
          title: 'Catalogus',
          noHeader: true,
          loader: async () => <CatalogBrowser />,
        },
      }),
      EntityHeaderLayoutBlueprint.make({
        params: {
          loader: () => import('./EntityHeader').then(module => module.EntityHeader),
        },
      }),
      EntityContentBlueprint.make({
        name: 'overview',
        params: {
          path: '/',
          title: 'Overzicht',
          group: 'overview',
          loader: () => import('./OverviewPage').then(module => <module.EntityOverzicht />),
        },
      }),
    ],
  }),
  createFrontendModule({
    pluginId: 'api-docs',
    extensions: [
      PageBlueprint.make({
        params: {
          path: '/api-docs',
          routeRef: apiDocsPlugin.routes.root,
          title: "API's",
          noHeader: true,
          loader: async () => <ApiBrowser />,
        },
      }),
    ],
  }),
  createFrontendModule({
    pluginId: 'scaffolder',
    extensions: [
      scaffolderPlugin.getExtension('sub-page:scaffolder/templates').override({
        *factory(originalFactory) {
          const original = originalFactory();
          yield* original;
          yield coreExtensionData.reactElement(
            <TemplateRoutes backstagePage={original.get(coreExtensionData.reactElement)} />,
          );
        },
      }),
      SubPageBlueprint.make({
        name: 'tasks',
        params: {
          path: 'tasks',
          title: 'Taken',
          loader: async () => <TaskRoutes />,
        },
      }),
    ],
  }),
  createFrontendModule({
    pluginId: 'techdocs',
    extensions: [
      PageBlueprint.make({
        params: {
          path: '/docs',
          routeRef: techdocsPlugin.routes.root,
          title: 'Documentatie',
          noHeader: true,
          loader: async () => <DocsBrowser />,
        },
      }),
    ],
  }),
];
